import { useState, useEffect, useCallback, useRef } from 'react';
import * as JsSIP from 'jssip';

export interface WebPhoneCredentials {
  username: string; // custid_extension
  password: string;
  extension: string;
  displayName: string;
  sbcServers: Array<{
    host: string;
    wsUri: string;
    priority: number;
  }>;
}

export interface CallSession {
  session: any;
  direction: 'incoming' | 'outgoing';
  remoteNumber: string;
  startTime: Date;
  duration: number;
  status: 'ringing' | 'connected' | 'ended';
}

export interface RTCStats {
  bytesReceived: number;
  bytesSent: number;
  receiveBitrate: number;
  sendBitrate: number;
  packetsReceived: number;
  packetsSent: number;
  packetsLost: number;
  packetsLostPercentage: string;
  jitter: string;
  roundTripTime: string;
  codec: string;
}

export type ConnectionStatus = 'disconnected' | 'connecting' | 'connected' | 'registered' | 'error';
export type CallStatus = 'idle' | 'calling' | 'ringing' | 'in-call';

export function useWebPhoneV2() {
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>('disconnected');
  const [callStatus, setCallStatus] = useState<CallStatus>('idle');
  const [currentCall, setCurrentCall] = useState<CallSession | null>(null);
  const [isMuted, setIsMuted] = useState(false);
  const [isOnHold, setIsOnHold] = useState(false);
  const [dialedNumber, setDialedNumber] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [audioDevices, setAudioDevices] = useState<{ inputDevices: MediaDeviceInfo[], outputDevices: MediaDeviceInfo[] }>({ inputDevices: [], outputDevices: [] });
  const [selectedSpeaker, setSelectedSpeaker] = useState<string>('default');
  const [selectedMicrophone, setSelectedMicrophone] = useState<string>('default');
  const [volume, setVolume] = useState<number>(70);
  const [rtcStats, setRtcStats] = useState<RTCStats | null>(null);
  const [eventLogs, setEventLogs] = useState<Array<{ timestamp: string; message: string; type: 'info' | 'success' | 'error' | 'warning' }>>([]);
  const [debugMode, setDebugMode] = useState<boolean>(() => localStorage.getItem('webphone_debug_mode') === 'true');

  const userAgentRef = useRef<any>(null);
  const currentSessionRef = useRef<any>(null);
  const remoteAudioRef = useRef<HTMLAudioElement | null>(null);
  const localAudioRef = useRef<HTMLAudioElement | null>(null);
  const ringtoneAudioRef = useRef<HTMLAudioElement | null>(null);
  const sbcHostRef = useRef<string>('sbc15.iatelecom.ca');

  // Add event log
  const addEventLog = useCallback((message: string, type: 'info' | 'success' | 'error' | 'warning' = 'info') => {
    const timestamp = new Date().toLocaleTimeString();
    setEventLogs(prev => [{ timestamp, message, type }, ...prev].slice(0, 100));
    console.log(`[WebPhoneV2] ${type.toUpperCase()}: ${message}`);
  }, []);

  // Enumerate audio devices
  const enumerateDevices = useCallback(async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const filterDevices = (deviceList: MediaDeviceInfo[]) => {
        const withLabels = deviceList.filter(device => device.label && device.label.trim() !== '');
        const uniqueDevices = withLabels.filter((device, index, self) =>
          index === self.findIndex(d => d.label === device.label)
        );
        return uniqueDevices;
      };

      const inputDevices = filterDevices(devices.filter(device => device.kind === 'audioinput'));
      const outputDevices = filterDevices(devices.filter(device => device.kind === 'audiooutput'));

      setAudioDevices({ inputDevices, outputDevices });
      addEventLog(`Found ${inputDevices.length} microphones and ${outputDevices.length} speakers`, 'info');
    } catch (error) {
      addEventLog('Failed to enumerate devices', 'error');
      console.error('[WebPhoneV2] Failed to enumerate devices:', error);
    }
  }, [addEventLog]);

  // Initialize audio elements - Based on working mobile-dialer-v3.html
  useEffect(() => {
    // Remote audio element
    const remoteAudio = new Audio();
    remoteAudio.id = 'webphone-remote-audio-v2';
    remoteAudio.autoplay = true;
    remoteAudio.volume = 0.7;
    remoteAudio.muted = false;
    remoteAudio.style.display = 'none';
    document.body.appendChild(remoteAudio);
    remoteAudioRef.current = remoteAudio;

    // Local audio element (for monitoring own voice)
    const localAudio = new Audio();
    localAudio.id = 'webphone-local-audio-v2';
    localAudio.autoplay = true;
    localAudio.muted = true; // Mute local to avoid feedback
    localAudio.style.display = 'none';
    document.body.appendChild(localAudio);
    localAudioRef.current = localAudio;

    // Ringtone audio element - simple sine wave ringtone
    const ringtoneAudio = new Audio();
    ringtoneAudio.id = 'webphone-ringtone-v2';
    ringtoneAudio.loop = true; // Loop the ringtone
    ringtoneAudio.volume = 0.5;
    ringtoneAudio.style.display = 'none';
    // Use a data URI for a simple ringtone (440Hz sine wave)
    ringtoneAudio.src = 'data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBSuAzvLZiTYIGGS57OihUBEJTqTh7q9iHAU7k9nzwokkBR98zPDhlkMLD1is5/O0ZBEFI4rU9MCENQoSTKPj6rFkHQQ5j9PyxoolBCd7yvDjmEYMDlam5O6xaRwGN4/R88KHJgQlec3y4ppHCxBZqufvtmYdBTWK0/PEiCUFKnvL8OGZRgsQWKzm77RmHAY4jtHyw4clBSt7yvDglUAKElas5e+xZhwGOY/T8sOIJAQresy/4JtGDBBXrOTwsWcdBTaM0fPAhiQFLH3M8OGaRQsRV6vl77Jm';
    document.body.appendChild(ringtoneAudio);
    ringtoneAudioRef.current = ringtoneAudio;

    addEventLog('Audio elements initialized', 'success');

    return () => {
      if (remoteAudio.parentNode) {
        remoteAudio.parentNode.removeChild(remoteAudio);
      }
      if (localAudio.parentNode) {
        localAudio.parentNode.removeChild(localAudio);
      }
      if (ringtoneAudio.parentNode) {
        ringtoneAudio.parentNode.removeChild(ringtoneAudio);
      }
    };
  }, [addEventLog]);

  // Play/stop ringtone based on call status
  useEffect(() => {
    if (callStatus === 'ringing' && currentCall?.direction === 'incoming' && ringtoneAudioRef.current) {
      // Start playing ringtone for incoming call
      ringtoneAudioRef.current.play().catch((e: Error) => {
        console.error('[WebPhoneV2] Failed to play ringtone:', e);
      });
    } else if (ringtoneAudioRef.current) {
      // Stop ringtone when not ringing
      ringtoneAudioRef.current.pause();
      ringtoneAudioRef.current.currentTime = 0;
    }
  }, [callStatus, currentCall?.direction]);

  // Update call duration timer
  useEffect(() => {
    if (callStatus === 'in-call' && currentCall) {
      const interval = setInterval(() => {
        const now = new Date();
        const elapsed = Math.floor((now.getTime() - currentCall.startTime.getTime()) / 1000);
        setCurrentCall(prev => prev ? { ...prev, duration: elapsed } : null);
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [callStatus, currentCall?.startTime]);

  // Toggle debug mode
  const toggleDebugMode = useCallback(() => {
    setDebugMode(prev => {
      const newValue = !prev;
      localStorage.setItem('webphone_debug_mode', String(newValue));
      if (newValue) {
        JsSIP.debug.enable('JsSIP:*');
        addEventLog('Debug mode enabled', 'info');
      } else {
        JsSIP.debug.disable();
        addEventLog('Debug mode disabled', 'info');
      }
      return newValue;
    });
  }, [addEventLog]);

  // Connect to WebRTC
  const connect = useCallback(async (credentials: WebPhoneCredentials) => {
    try {
      console.log('[WebPhoneV2] Connecting with credentials:', credentials);
      addEventLog(`Connecting to ${credentials.sbcServers[0].host}...`, 'info');
      setConnectionStatus('connecting');

      // Enable debug if needed
      if (debugMode) {
        JsSIP.debug.enable('JsSIP:*');
      }

      const wsUri = credentials.sbcServers[0].wsUri;
      sbcHostRef.current = credentials.sbcServers[0].host;

      const socket = new JsSIP.WebSocketInterface(wsUri);

      const configuration = {
        sockets: [socket],
        uri: `sip:${credentials.username}@${credentials.sbcServers[0].host}`,
        password: credentials.password,
        display_name: credentials.displayName || credentials.extension,
        register: true,
        session_timers: false,
        register_expires: 600,
      };

      const ua = new JsSIP.UA(configuration);

      // UA event handlers
      ua.on('connecting', () => {
        console.log('[WebPhoneV2] Connecting to WebSocket...');
        addEventLog('Connecting to WebSocket...', 'info');
      });

      ua.on('connected', () => {
        console.log('[WebPhoneV2] WebSocket connected');
        addEventLog('WebSocket connected', 'success');
        setConnectionStatus('connected');
      });

      ua.on('disconnected', () => {
        console.log('[WebPhoneV2] WebSocket disconnected');
        addEventLog('Disconnected from server', 'error');
        setConnectionStatus('disconnected');
      });

      ua.on('registered', () => {
        console.log('[WebPhoneV2] Successfully registered');
        addEventLog(`Registered as ${credentials.extension}`, 'success');
        setConnectionStatus('registered');
      });

      ua.on('registrationFailed', (e: any) => {
        console.error('[WebPhoneV2] Registration failed:', e);
        addEventLog(`Registration failed: ${e.cause}`, 'error');
        setConnectionStatus('error');
        setErrorMessage('Registration failed');
      });

      ua.on('newRTCSession', (e: any) => {
        const session = e.session;

        if (session.direction === 'incoming') {
          const callerNumber = session.remote_identity.uri.user;
          console.log('[WebPhoneV2] === INCOMING CALL RECEIVED ===');
          console.log('[WebPhoneV2] Caller:', callerNumber);
          console.log('[WebPhoneV2] currentSessionRef.current exists?', !!currentSessionRef.current);

          // Check if we're already on a call - silently reject with busy
          // Only check currentSessionRef, not callStatus (which is stale in this callback)
          if (currentSessionRef.current) {
            // Verify the session is actually active before rejecting
            const currentStatus = currentSessionRef.current.status;
            console.log('[WebPhoneV2] Current session status:', currentStatus);

            // Only reject if current session is actually active (not ended/terminated)
            // JsSIP statuses: 'STATUS_NULL', 'STATUS_INVITE_SENT', 'STATUS_1XX_RECEIVED', 'STATUS_INVITE_RECEIVED',
            // 'STATUS_WAITING_FOR_ANSWER', 'STATUS_ANSWERED', 'STATUS_WAITING_FOR_ACK', 'STATUS_CANCELED',
            // 'STATUS_TERMINATED', 'STATUS_CONFIRMED'
            const activeStatuses = ['STATUS_INVITE_SENT', 'STATUS_1XX_RECEIVED', 'STATUS_INVITE_RECEIVED',
                                   'STATUS_WAITING_FOR_ANSWER', 'STATUS_ANSWERED', 'STATUS_WAITING_FOR_ACK',
                                   'STATUS_CONFIRMED'];

            if (activeStatuses.includes(String(currentStatus))) {
              console.log('[WebPhoneV2] Session is active, rejecting incoming call with busy');
              addEventLog(`Busy: Rejected call from ${callerNumber}`, 'warning');
              // Silently reject with 486 Busy Here
              session.terminate({
                status_code: 486,
                reason_phrase: 'Busy Here'
              });
              return;
            } else {
              // Session is terminated/canceled, clear it
              console.log('[WebPhoneV2] Current session is not active, clearing stale reference');
              currentSessionRef.current = null;
            }
          }

          console.log('[WebPhoneV2] Accepting incoming call...');
          addEventLog(`Incoming call from ${callerNumber}`, 'info');

          // Update UI state to show incoming call
          console.log('[WebPhoneV2] Setting call status to ringing...');
          setCallStatus('ringing');
          setDialedNumber(callerNumber);

          // Set currentCall so the Answer button appears
          console.log('[WebPhoneV2] Setting currentCall...');
          setCurrentCall({
            session,
            direction: 'incoming',
            remoteNumber: callerNumber,
            startTime: new Date(),
            duration: 0,
            status: 'ringing'
          });

          console.log('[WebPhoneV2] Setting currentSessionRef...');
          currentSessionRef.current = session;

          console.log('[WebPhoneV2] Setting up session handlers...');
          setupSessionHandlers(session, 'incoming', callerNumber);

          console.log('[WebPhoneV2] === INCOMING CALL SETUP COMPLETE ===');
          // Don't auto-answer - wait for user to click Answer button
        }
      });

      userAgentRef.current = ua;
      ua.start();

      addEventLog('User agent started', 'success');
    } catch (error) {
      console.error('[WebPhoneV2] Connect failed:', error);
      addEventLog('Connection failed', 'error');
      setConnectionStatus('error');
      setErrorMessage('Failed to connect');
    }
  }, [debugMode, addEventLog]);

  // Setup session handlers - Based on working mobile-dialer-v3.html
  const setupSessionHandlers = useCallback((session: any, direction: 'incoming' | 'outgoing', number: string) => {
    console.log('[WebPhoneV2] Setting up session handlers for', direction, 'call to', number);

    // CRITICAL: peerconnection handler for audio attachment
    session.on('peerconnection', (e: any) => {
      console.log('[WebPhoneV2] ========== PEERCONNECTION EVENT ==========');
      addEventLog('Peer connection established', 'info');

      const peerconnection = e.peerconnection;

      console.log('[WebPhoneV2] PeerConnection state:', peerconnection.connectionState);
      console.log('[WebPhoneV2] ICE connection state:', peerconnection.iceConnectionState);

      // Monitor state changes
      peerconnection.onconnectionstatechange = () => {
        console.log('[WebPhoneV2] Connection state changed to:', peerconnection.connectionState);
        addEventLog(`Connection state: ${peerconnection.connectionState}`, 'info');
      };

      peerconnection.oniceconnectionstatechange = () => {
        console.log('[WebPhoneV2] ICE connection state changed to:', peerconnection.iceConnectionState);
        addEventLog(`ICE state: ${peerconnection.iceConnectionState}`, 'info');
      };

      // Handle incoming audio tracks (modern API) - THIS IS THE KEY!
      peerconnection.ontrack = (event: RTCTrackEvent) => {
        console.log('[WebPhoneV2] ========== ONTRACK FIRED ==========');
        console.log('[WebPhoneV2] Remote track received:', event.track.kind);
        addEventLog(`Remote ${event.track.kind} track received`, 'success');

        if (event.streams && event.streams[0] && event.track.kind === 'audio') {
          if (remoteAudioRef.current) {
            if (!remoteAudioRef.current.srcObject || (remoteAudioRef.current.srcObject as MediaStream).id !== event.streams[0].id) {
              remoteAudioRef.current.srcObject = event.streams[0];
              console.log('[WebPhoneV2] Remote audio stream attached');
              addEventLog('Remote audio attached successfully', 'success');

              remoteAudioRef.current.play().then(() => {
                console.log('[WebPhoneV2] Audio playback started successfully');
                addEventLog('Audio playback started', 'success');
              }).catch((e: Error) => {
                console.error('[WebPhoneV2] Audio play error:', e.message);
                addEventLog(`Audio play error: ${e.message}`, 'error');
              });
            }
          }
        }
      };

      // Fallback for older browsers
      peerconnection.onaddstream = (event: any) => {
        console.log('[WebPhoneV2] Remote audio stream received (legacy onaddstream)');
        addEventLog('Remote audio received (legacy)', 'info');

        const audioTracks = event.stream.getAudioTracks();
        console.log('[WebPhoneV2] Audio tracks in stream:', audioTracks.length);

        if (audioTracks.length > 0 && remoteAudioRef.current) {
          remoteAudioRef.current.srcObject = event.stream;
          console.log('[WebPhoneV2] Remote audio stream attached (legacy)');
          addEventLog('Remote audio attached (legacy)', 'success');

          remoteAudioRef.current.play().then(() => {
            console.log('[WebPhoneV2] Audio playback started (legacy)');
          }).catch((e: Error) => {
            console.error('[WebPhoneV2] Audio play error:', e.message);
          });
        }
      };

      // Get local audio stream
      const senders = peerconnection.getSenders();
      console.log('[WebPhoneV2] Local senders count:', senders.length);

      senders.forEach((sender: RTCRtpSender, index: number) => {
        if (sender.track) {
          console.log('[WebPhoneV2] Sender', index, '- kind:', sender.track.kind);
        }
      });

      const audioSender = senders.find((sender: RTCRtpSender) => sender.track && sender.track.kind === 'audio');
      if (audioSender && audioSender.track && localAudioRef.current) {
        const localStream = new MediaStream([audioSender.track]);
        localAudioRef.current.srcObject = localStream;
        console.log('[WebPhoneV2] Local audio stream attached');
        addEventLog('Local audio attached', 'info');
      }
    });

    // Confirmed handler - double-check audio attachment
    session.on('confirmed', () => {
      console.log('[WebPhoneV2] ========== CONFIRMED EVENT ==========');
      addEventLog('Call confirmed - media flowing', 'success');
      setCallStatus('in-call');

      // Update call session
      setCurrentCall({
        session,
        direction,
        remoteNumber: number,
        startTime: new Date(),
        duration: 0,
        status: 'connected'
      });

      // Double-check remote audio attachment
      if (session.connection) {
        const pc = session.connection;
        const receivers = pc.getReceivers();
        console.log('[WebPhoneV2] Remote receivers count:', receivers.length);

        receivers.forEach((receiver: RTCRtpReceiver, index: number) => {
          if (receiver.track) {
            console.log('[WebPhoneV2] Receiver', index, '- kind:', receiver.track.kind, 'enabled:', receiver.track.enabled, 'muted:', receiver.track.muted);

            // Manually attach remote audio if not already done
            if (receiver.track.kind === 'audio' && remoteAudioRef.current && !remoteAudioRef.current.srcObject) {
              const stream = new MediaStream([receiver.track]);
              remoteAudioRef.current.srcObject = stream;
              console.log('[WebPhoneV2] Manually attached remote audio in confirmed');
              addEventLog('Remote audio manually attached', 'warning');

              remoteAudioRef.current.play().then(() => {
                console.log('[WebPhoneV2] Manual audio playback started');
              }).catch((e: Error) => {
                console.error('[WebPhoneV2] Manual audio play error:', e.message);
              });
            }
          }
        });
      }

      // Final check
      if (remoteAudioRef.current?.srcObject) {
        console.log('[WebPhoneV2] ✓ Remote audio srcObject is set');
        addEventLog('✓ Remote audio confirmed', 'success');
      } else {
        console.log('[WebPhoneV2] ✗ WARNING: Remote audio srcObject is NOT set!');
        addEventLog('✗ Remote audio NOT set', 'error');
      }
    });

    // Progress handler
    session.on('progress', () => {
      console.log('[WebPhoneV2] Call is in progress (ringing)...');
      addEventLog(`${direction === 'outgoing' ? 'Calling' : 'Ringing'}...`, 'info');
      setCallStatus('ringing');
    });

    // Failed handler
    session.on('failed', (e: any) => {
      console.log('[WebPhoneV2] Call failed:', e.cause);
      addEventLog(`Call failed: ${e.cause}`, 'error');
      setCallStatus('idle');
      setCurrentCall(null);
      currentSessionRef.current = null;
      if (remoteAudioRef.current) remoteAudioRef.current.srcObject = null;
      if (localAudioRef.current) localAudioRef.current.srcObject = null;
    });

    // Ended handler
    session.on('ended', (e: any) => {
      console.log('[WebPhoneV2] Call ended:', e.cause);
      addEventLog(`Call ended: ${e.cause}`, 'info');
      setCallStatus('idle');
      setCurrentCall(null);
      currentSessionRef.current = null;
      if (remoteAudioRef.current) remoteAudioRef.current.srcObject = null;
      if (localAudioRef.current) localAudioRef.current.srcObject = null;
    });

  }, [addEventLog]);

  // Make a call - Based on working mobile-dialer-v3.html
  const makeCall = useCallback((number: string) => {
    if (!userAgentRef.current) {
      console.error('[WebPhoneV2] User agent not initialized');
      addEventLog('Not connected to server', 'error');
      return;
    }

    const sipUri = `sip:${number}@${sbcHostRef.current}`;
    console.log('[WebPhoneV2] Calling:', sipUri);
    addEventLog(`Calling ${number}...`, 'info');
    setCallStatus('calling');

    // CRITICAL: Event handlers must be passed BEFORE call()
    const eventHandlers = {
      progress: () => {
        console.log('[WebPhoneV2] Call is in progress');
      },
      failed: (e: any) => {
        console.log('[WebPhoneV2] Call failed:', e);
      },
      ended: (e: any) => {
        console.log('[WebPhoneV2] Call ended:', e);
      },
      accepted: () => {
        console.log('[WebPhoneV2] Call accepted');
      },
    };

    const options = {
      eventHandlers,
      mediaConstraints: {
        audio: { channelCount: 1 },
        video: false
      },
      pcConfig: {
        iceServers: [
          { urls: ['stun:stun.l.google.com:19302'] }
        ]
      },
      rtcOfferConstraints: {
        offerToReceiveAudio: true,
        offerToReceiveVideo: false
      }
    };

    const session = userAgentRef.current.call(sipUri, options);
    currentSessionRef.current = session;
    setupSessionHandlers(session, 'outgoing', number);
  }, [setupSessionHandlers, addEventLog]);

  // Disconnect
  const disconnect = useCallback(() => {
    if (currentSessionRef.current) {
      currentSessionRef.current.terminate();
      currentSessionRef.current = null;
    }
    if (userAgentRef.current) {
      userAgentRef.current.stop();
      userAgentRef.current = null;
    }
    addEventLog('Disconnected', 'info');
    setConnectionStatus('disconnected');
    setCallStatus('idle');
    setCurrentCall(null);
  }, [addEventLog]);

  // Answer call
  const answerCall = useCallback(() => {
    if (currentSessionRef.current) {
      console.log('[WebPhoneV2] Answering incoming call');
      addEventLog('Answering call...', 'info');

      currentSessionRef.current.answer({
        mediaConstraints: {
          audio: { channelCount: 1 },
          video: false
        },
        pcConfig: {
          iceServers: [
            { urls: ['stun:stun.l.google.com:19302'] }
          ]
        },
        rtcAnswerConstraints: {
          offerToReceiveAudio: true,
          offerToReceiveVideo: false
        }
      });
    }
  }, [addEventLog]);

  // Hang up
  const hangUp = useCallback(() => {
    if (currentSessionRef.current) {
      currentSessionRef.current.terminate();
      addEventLog('Call ended', 'info');
    }
  }, [addEventLog]);

  // Toggle mute
  const toggleMute = useCallback(() => {
    if (currentSessionRef.current) {
      if (isMuted) {
        currentSessionRef.current.unmute();
        addEventLog('Microphone unmuted', 'info');
      } else {
        currentSessionRef.current.mute();
        addEventLog('Microphone muted', 'info');
      }
      setIsMuted(!isMuted);
    }
  }, [isMuted, addEventLog]);

  // Toggle hold
  const toggleHold = useCallback(() => {
    if (currentSessionRef.current) {
      if (isOnHold) {
        currentSessionRef.current.unhold();
        addEventLog('Call resumed', 'info');
      } else {
        currentSessionRef.current.hold();
        addEventLog('Call on hold', 'info');
      }
      setIsOnHold(!isOnHold);
    }
  }, [isOnHold, addEventLog]);

  // Add digit to dialed number
  const addDigit = useCallback((digit: string) => {
    setDialedNumber(prev => prev + digit);
    // Send DTMF if in call
    if (currentSessionRef.current && callStatus === 'in-call') {
      currentSessionRef.current.sendDTMF(digit);
      addEventLog(`DTMF: ${digit}`, 'info');
    }
  }, [callStatus, addEventLog]);

  // Delete last digit (new feature from mobile-dialer-v3)
  const deleteDigit = useCallback(() => {
    setDialedNumber(prev => prev.slice(0, -1));
  }, []);

  // Clear number
  const clearNumber = useCallback(() => {
    setDialedNumber('');
  }, []);

  // Change speaker
  const changeSpeaker = useCallback(async (deviceId: string) => {
    if (remoteAudioRef.current && typeof (remoteAudioRef.current as any).setSinkId === 'function') {
      try {
        await (remoteAudioRef.current as any).setSinkId(deviceId);
        setSelectedSpeaker(deviceId);
        addEventLog('Speaker changed', 'success');
      } catch (error) {
        console.error('[WebPhoneV2] Failed to change speaker:', error);
        addEventLog('Failed to change speaker', 'error');
      }
    }
  }, [addEventLog]);

  // Change microphone
  const changeMicrophone = useCallback((deviceId: string) => {
    setSelectedMicrophone(deviceId);
    addEventLog('Microphone changed', 'success');
  }, [addEventLog]);

  // Change volume
  const changeVolume = useCallback((newVolume: number) => {
    setVolume(newVolume);
    if (remoteAudioRef.current) {
      remoteAudioRef.current.volume = newVolume / 100;
    }
  }, []);

  // Test speaker
  const testSpeaker = useCallback(async () => {
    const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBSuAzvLZiTYIGGS57OihUBEJTqTh7q9iHAU7k9nzwokkBR98zPDhlkMLD1is5/O0ZBEFI4rU9MCENQoSTKPj6rFkHQQ5j9PyxoolBCd7yvDjmEYMDlam5O6xaRwGN4/R88KHJgQlec3y4ppHCxBZqufvtmYdBTWK0/PEiCUFKnvL8OGZRgsQWKzm77RmHAY4jtHyw4clBSt7yvDglUAKElas5e+xZhwGOY/T8sOIJAQresy/4JtGDBBXrOTwsWcdBTaM0fPAhiQFLH3M8OGaRQsRV6vl77Jm');
    audio.volume = volume / 100;
    try {
      if (typeof (audio as any).setSinkId === 'function') {
        await (audio as any).setSinkId(selectedSpeaker);
      }
      audio.play();
      addEventLog('Testing speaker...', 'info');
    } catch (error) {
      console.error('[WebPhoneV2] Failed to test speaker:', error);
      addEventLog('Failed to test speaker', 'error');
    }
  }, [volume, selectedSpeaker, addEventLog]);

  return {
    connectionStatus,
    callStatus,
    currentCall,
    isMuted,
    isOnHold,
    dialedNumber,
    errorMessage,
    audioDevices,
    selectedSpeaker,
    selectedMicrophone,
    volume,
    rtcStats,
    eventLogs,
    debugMode,
    connect,
    disconnect,
    makeCall,
    answerCall,
    hangUp,
    toggleMute,
    toggleHold,
    addDigit,
    deleteDigit, // NEW: Delete last digit instead of clear all
    clearNumber,
    changeSpeaker,
    changeMicrophone,
    changeVolume,
    enumerateDevices,
    testSpeaker,
    toggleDebugMode,
  };
}
